import React, { useRef, useCallback } from 'react';
import { Link } from 'gatsby';
import Helmet from 'react-helmet';

import Layout from '../../../components/Layout';
import Icon from '../../../components/Icon';

import '@fontsource/inter';
import "@fontsource/inter/200.css";
import "@fontsource/inter/600.css";

import './argo.less';

import HubspotForm from 'react-hubspot-form';

function Argo({ location }) {
  const continuousDeliveryRef = useRef();
  const rolloutsRef = useRef();
  const workflowsRef = useRef();
  const eventsRef = useRef();

  const handleContinuousDeliveryClick = useCallback(
    () => {
      continuousDeliveryRef.current.scrollIntoView({
        behavior: 'smooth',
        block: "center"
      });
    },
    [continuousDeliveryRef],
  );

  const handleRolloutsRefClick = useCallback(
    () => {
      rolloutsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: "center"
      });
    },
    [rolloutsRef],
  );

  const handleWorkflowsRefClick = useCallback(
    () => {
      workflowsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: "center"
      });
    },
    [workflowsRef],
  );

  const handleEventsRefClick = useCallback(
    () => {
      eventsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: "center"
      });
    },
    [eventsRef],
  );

  return (
    <Layout location={location} >
      <Helmet>
        <title>Argo 101: What it is and why it matters</title>
        <meta name="description" content="Argo is a CNCF project for building and managing continuous delivery workflows on Kubernetes. The Argo Project consists of four distinct projects: continuous delivery, rollouts, workflows & pipelines, and events." />
        <meta name="og:type" content="website" />
        <meta name="author" content="Datawire.io" />
      </Helmet>

      <div className="argo">
        <section className="argo__container argo__information">
          <div className="argo__information-content">
            <div>
              <p className="argo__information-note">
                What? &amp; Why?
              </p>
              <h1 className="argo__information-title">
                Argo 101: <br />
                <mark className="argo__mark">What</mark> it is, and <mark className="argo__mark">why</mark> it matters
              </h1>
              <h2 className="argo__information-description">
                Argo is a <Link to="https://www.cncf.io" className="argo__information-cncf" target="_blank">CNCF</Link> project for building and managing continuous delivery workflows on Kubernetes.
              </h2>
            </div>
            <div>
              <Icon name="argo" className="argo__argo-image" />
            </div>
          </div>
        </section>

        <section className="argo__container argo__project">
          <div className="argo__project-information">
            <div className="argo__project-left">
              <span>
                The Argo Project consists of four distinct projects
                </span>
            </div>
            <div className="argo__project-right">
              <div
                onClick={handleContinuousDeliveryClick}
                className="argo__project-item">
                <Icon name="number-one" />
                Continuous Delivery (CD)
              </div>
              <div
                onClick={handleRolloutsRefClick}
                className="argo__project-item">
                <Icon name="number-two" />
                Rollouts
              </div>
              <div
                onClick={handleWorkflowsRefClick}
                className="argo__project-item">
                <Icon name="number-three" />
                Workflows &amp; Pipelines
              </div>
              <div
                onClick={handleEventsRefClick}
                className="argo__project-item">
                <Icon name="number-four" />
                Events
              </div>
            </div>
          </div>
        </section>

        <section className="argo__container argo__cards">
          <div
            className="argo__card"
            ref={continuousDeliveryRef}
          >
            <Icon name="number-1" className="argo__card-icon" />
            <h2 className="argo__card-title">
              Continuous Delivery (CD)
            </h2>
            <p className="argo__card-description">
              Argo CD automates the deployment of applications and services from source control to Kubernetes.
            </p>
            <div className="argo__card-container">
              <div className="argo__card-left">
                <p className="argo__card-note">
                  Example use case
                </p>
                <p className="argo__card-text-source">
                  Implement GitOps-style workflow for managing application delivery.
                </p>
              </div>
              <div className="argo__card-right">
                <p className="argo__card-note">
                  Resources
                </p>
                <Link
                  to="https://github.com/argoproj/argo-cd"
                  className="argo__card-item"
                  target="_blank">
                  <Icon name="github" />
                    Repository
                </Link>
                <Link
                  to="https://argo-cd.readthedocs.io/en/stable"
                  className="argo__card-item"
                  target="_blank">
                  <Icon name="document" />
                    Documentation
                </Link>
              </div>
            </div>

            <Link
              to="https://argo-cd.readthedocs.io/en/stable/getting_started"
              className="argo__button"
              target="_blank">
              GET STARTED
              <Icon name="arrow" />
            </Link>
          </div>

          <div className="argo__card" ref={rolloutsRef}>
            <Icon name="number-2" className="argo__card-icon" />
            <h2 className="argo__card-title">
              Rollouts
            </h2>
            <p className="argo__card-description">
              Argo Rollouts implements progressive delivery strategies for Kubernetes microservices.
            </p>
            <div className="argo__card-container">
              <div className="argo__card-left">
                <p className="argo__card-note">
                  Example use case
                </p>
                <p className="argo__card-text-source">
                  Canary release a new version of a service, orchestrated by your API Gateway.
                </p>
              </div>
              <div className="argo__card-right">
                <p className="argo__card-note">
                  Resources
                </p>
                <Link
                  to="https://github.com/argoproj/argo-rollouts"
                  className="argo__card-item"
                  target="_blank">
                  <Icon name="github" />
                  Repository
                </Link>
                <Link
                  to="https://argoproj.github.io/argo-rollouts"
                  className="argo__card-item"
                  target="_blank">
                  <Icon name="document" />
                  Documentation
                </Link>
              </div>
            </div>

            <Link
              to="https://argoproj.github.io/argo-rollouts/getting-started"
              className="argo__button"
              target="_blank">
              GET STARTED
              <Icon name="arrow" />
            </Link>
          </div>

          <div className="argo__card" ref={workflowsRef}>
            <Icon name="number-3" className="argo__card-icon" />
            <h2 className="argo__card-title">
              Workflows &amp; Pipelines
            </h2>
            <p className="argo__card-description">
              Argo Workflows manages tasks that need to be executed in parallel or sequence on Kubernetes.
            </p>
            <div className="argo__card-container">
              <div className="argo__card-left">
                <p className="argo__card-note">
                  Example use case
                </p>
                <p className="argo__card-text-source">
                  Efficiently run compute-intensive jobs for data science.
                </p>
              </div>
              <div className="argo__card-right">
                <p className="argo__card-note">
                  Resources
                </p>
                <Link
                  to="https://github.com/argoproj/argo-workflows"
                  className="argo__card-item"
                  target="_blank">
                  <Icon name="github" />
                  Repository
                </Link>
                <Link
                  to="https://argoproj.github.io/argo-workflows"
                  className="argo__card-item"
                  target="_blank">
                  <Icon name="document" />
                  Documentation
                </Link>
              </div>
            </div>

            <Link
              to="https://argoproj.github.io/argo-workflows/quick-start"
              className="argo__button"
              target="_blank">
              GET STARTED
              <Icon name="arrow" />
            </Link>
          </div>

          <div className="argo__card" ref={eventsRef}>
            <Icon name="number-4" className="argo__card-icon" />
            <h2 className="argo__card-title">
              Events
            </h2>
            <p className="argo__card-description">
              Argo Events triggers specific actions (e.g., Argo Workflows) based on specific events.
            </p>
            <div className="argo__card-container">
              <div className="argo__card-left">
                <p className="argo__card-note">
                  Example use case
                </p>
                <p className="argo__card-text-source">
                  Run a serverless function to compress an image every time a new image is uploaded to an S3 bucket.
                </p>
              </div>
              <div className="argo__card-right">
                <p className="argo__card-note">
                  Resources
                </p>
                <Link
                  to="https://github.com/argoproj/argo-events"
                  className="argo__card-item"
                  target="_blank">
                  <Icon name="github" />
                  Repository
                </Link>
                <Link
                  to="https://argoproj.github.io/argo-events"
                  className="argo__card-item"
                  target="_blank">
                  <Icon name="document" />
                  Documentation
                </Link>
              </div>
            </div>

            <Link
              to="https://argoproj.github.io/argo-events/quick_start"
              className="argo__button"
              target="_blank">
              GET STARTED
              <Icon name="arrow" />
            </Link>
          </div>
        </section>


        <section id="early-adopter" className="argo__container">
          <div className="argo__more-information">
            <div className="argo__more-information-container-title">
              <Icon name="information" className="argo__more-information-icon" />
              Apply for the Early Adopter Program
            </div>
            <p>
              We’re working to integrate <Link to="/products/edge-stack/api-gateway" target="_blank">Ambassador Edge Stack</Link> directly into the continuous delivery workflow with Argo.
            </p>
            <h4>
              Apply for the Early Adopter Program today to:
            </h4>
            <ul>
              <li className="argo__more-information-item">
                Collaborate directly with the Ambassador engineering team on continuous delivery and your use case
              </li>
              <li className="argo__more-information-item">
                Get early access to new ehanced functionality for canary workflows with Edge Stack and Argo
              </li>
              <li className="argo__more-information-item">
                Share your feedback on new features and supported use cases
              </li>
            </ul>
            <div className="argo__more-information-item-form">
              <HubspotForm
                portalId='485087'
                formId='7d072319-04bf-491d-9c0d-c8c696a57007'
                onSubmit={() => console.log('Submit!')}
                onReady={(form) => console.log('Form ready!')}
                loading={<div>Loading...</div>}
              />
            </div>
          </div>
        </section>
      </div>
    </Layout >
  )
}

export default Argo;
